import { maskMyNumber } from '../GeneralProfile/useGeneralProfileFlow';
import {
  changeReasonLabelKey,
  isSpouseChangeReasonCode,
} from '../dependentDeduction/spouseSection/spouseChangeReason';
import { formatOtherIncomeChangeReasonDisplay } from '../dependentDeduction/otherIncomeEarner/otherIncomeEarnerDisplay';
import { disabilityLabel } from '../dependentDeduction/spouseSection/spouseSectionDisplay';
import {
  isDependentChangeReasonCode,
  isDependentNonResidentReasonCode,
  isUnder16AtTaxYearEnd,
  taxDependentCategoryLabel,
} from './familyInfoDependentEligibility';
import { isSpouseTaxFlow } from './familyInfoSpouseEligibility';
import type { FamilyMemberDraft } from './familyInfoTypes';

function yesNoLabel(t: (k: string) => string, v: string): string {
  if (v === 'yes') return t('submitDocuments.familyInfo.yes');
  if (v === 'no') return t('submitDocuments.familyInfo.no');
  return '—';
}

function relLabel(t: (k: string) => string, r: string): string {
  if (!r) return '—';
  return t(`submitDocuments.familyInfo.relationship.${r}`);
}

export function buildMemberSummary(d: FamilyMemberDraft, t: (k: string) => string): string {
  const lines: string[] = [];
  lines.push(`${t('submitDocuments.familyInfo.summary.relationship')}: ${relLabel(t, d.relationship)}`);
  lines.push(`${t('submitDocuments.familyInfo.summary.fullName')}: ${(d.fullName ?? '').trim()}`);
  lines.push(`${t('submitDocuments.familyInfo.summary.fullNameKana')}: ${(d.fullNameKana ?? '').trim()}`);
  lines.push(`${t('submitDocuments.familyInfo.summary.dob')}: ${d.dateOfBirth || '—'}`);
  if ((d.myNumber ?? '').trim()) {
    lines.push(`${t('submitDocuments.familyInfo.summary.myNumber')}: ${maskMyNumber(d.myNumber)}`);
  }
  lines.push(
    `${t('submitDocuments.familyInfo.summary.sameHousehold')}: ${yesNoLabel(t, d.livingInSameHousehold)}`
  );
  if (d.livingInSameHousehold === 'no' && d.ifNotAddress.trim()) {
    lines.push(`${t('submitDocuments.familyInfo.summary.address')}:`);
    d.ifNotAddress
      .trim()
      .split(/\r?\n/)
      .forEach((line) => lines.push(line));
    if (d.relationship === 'spouse') {
      lines.push(
        `${t('submitDocuments.familyInfo.steps.outsideJapan')}: ${yesNoLabel(t, d.outsideJapan)}`
      );
    }
  }

  if (d.claimTaxDependent === 'yes' || d.claimTaxDependent === 'no') {
    lines.push(
      `${t('submitDocuments.familyInfo.steps.taxGate')}: ${yesNoLabel(t, d.claimTaxDependent)}`
    );
  }

  if (d.claimTaxDependent === 'yes') {
    lines.push(`— ${t('submitDocuments.familyInfo.summary.taxSection')} —`);
    if (isSpouseTaxFlow(d)) {
      if (d.sharedLivelihood) {
        lines.push(
          `${t('submitDocuments.dependentDeduction.spouse.steps.sharedLivelihood')}: ${yesNoLabel(t, d.sharedLivelihood)}`
        );
      }
      if (d.spouseFamilyWorker) {
        lines.push(
          `${t('submitDocuments.dependentDeduction.spouse.steps.spouseFamilyWorker')}: ${yesNoLabel(t, d.spouseFamilyWorker)}`
        );
      }
      if (d.estimatedIncome.trim() && !isUnder16AtTaxYearEnd(d.dateOfBirth, d.taxYear)) {
        lines.push(
          `${t('submitDocuments.dependentDeduction.spouse.steps.spouseEstimatedIncome')}: ${d.estimatedIncome.trim()}`
        );
      }
      if (d.disabilityCategory) {
        lines.push(
          `${t('submitDocuments.dependentDeduction.spouse.steps.disabilityStatus')}: ${disabilityLabel(t, d.disabilityCategory)}`
        );
      }
      if (d.disabilityFacts.trim()) {
        lines.push(
          `${t('submitDocuments.profileName.bubble.disabilityFacts')}: ${d.disabilityFacts.trim()}`
        );
      }
      if (d.changeDuringYear) {
        lines.push(
          `${t('submitDocuments.dependentDeduction.spouse.steps.changeDuringYear')}: ${yesNoLabel(t, d.changeDuringYear)}`
        );
      }
      if (d.changeDuringYear === 'yes' && (d.taxChangeDate.trim() || d.taxChangeReason.trim())) {
        const parts: string[] = [];
        if (d.taxChangeReason.trim()) {
          parts.push(
            isSpouseChangeReasonCode(d.taxChangeReason)
              ? t(changeReasonLabelKey(d.taxChangeReason))
              : formatOtherIncomeChangeReasonDisplay(t, d.taxChangeReason)
          );
        }
        if (d.taxChangeDate.trim()) parts.push(d.taxChangeDate.trim());
        lines.push(`${t('submitDocuments.familyInfo.summary.taxChange')}: ${parts.join(' / ')}`);
      }
    } else {
      if (d.sharedLivelihood) {
        lines.push(
          `${t('submitDocuments.dependentDeduction.dependents.steps.sharedLivelihood')}: ${yesNoLabel(t, d.sharedLivelihood)}`
        );
      }
      if (d.taxIneligibilityReason) {
        lines.push(
          `${t('submitDocuments.dependentDeduction.dependents.summary.ineligible')}: ${t(
            `submitDocuments.dependentDeduction.dependents.ineligibleReason.${d.taxIneligibilityReason}`
          )}`
        );
      } else {
        if (d.estimatedIncome.trim() && !isUnder16AtTaxYearEnd(d.dateOfBirth, d.taxYear)) {
          lines.push(
            `${t('submitDocuments.dependentDeduction.dependents.steps.estimatedIncome')}: ${d.estimatedIncome.trim()}`
          );
        }
        if (d.taxDependentCategory.trim()) {
          lines.push(
            `${t('submitDocuments.dependentDeduction.dependents.summary.categories')}: ${taxDependentCategoryLabel(
              d.taxDependentCategory.trim(),
              t
            )}`
          );
        }
        if (d.taxNonResident) {
          lines.push(
            `${t('submitDocuments.dependentDeduction.dependents.summary.nonResident')}: ${yesNoLabel(t, d.taxNonResident)}`
          );
        }
        if (d.nonResidentReason) {
          const reasonLabel = isDependentNonResidentReasonCode(d.nonResidentReason)
            ? t(
                `submitDocuments.dependentDeduction.dependents.nonResidentReason.option.${d.nonResidentReason}`
              )
            : d.nonResidentReason.trim();
          lines.push(
            `${t('submitDocuments.dependentDeduction.dependents.summary.nonResidentReason')}: ${reasonLabel}`
          );
        }
        if (d.remittanceAmount.trim()) {
          lines.push(
            `${t('submitDocuments.dependentDeduction.dependents.summary.remittanceAmount')}: ${d.remittanceAmount.trim()}`
          );
        }
        if (d.disabilityCategory) {
          lines.push(
            `${t('submitDocuments.dependentDeduction.dependents.steps.disabilityStatus')}: ${disabilityLabel(t, d.disabilityCategory)}`
          );
        }
        if (d.disabilityFacts.trim()) {
          lines.push(
            `${t('submitDocuments.profileName.bubble.disabilityFacts')}: ${d.disabilityFacts.trim()}`
          );
        }
        if (d.changeDuringYear) {
          lines.push(
            `${t('submitDocuments.dependentDeduction.dependents.steps.changeDuringYear')}: ${yesNoLabel(t, d.changeDuringYear)}`
          );
        }
        if (d.changeDuringYear === 'yes' && (d.taxChangeDate.trim() || d.taxChangeReason.trim())) {
          const parts: string[] = [];
          if (d.taxChangeReason.trim()) {
            parts.push(
              isDependentChangeReasonCode(d.taxChangeReason)
                ? t(
                    `submitDocuments.dependentDeduction.dependents.changeReason.option.${d.taxChangeReason}`
                  )
                : formatOtherIncomeChangeReasonDisplay(t, d.taxChangeReason)
            );
          }
          if (d.taxChangeDate.trim()) parts.push(d.taxChangeDate.trim());
          lines.push(`${t('submitDocuments.familyInfo.summary.taxChange')}: ${parts.join(' / ')}`);
        }
      }
    }
  }

  if (d.insuranceRequired === 'yes' || d.insuranceRequired === 'no') {
    lines.push(`— ${t('submitDocuments.familyInfo.summary.insuranceSection')} —`);
    lines.push(
      `${t('submitDocuments.familyInfo.insuranceRequired')}: ${
        d.insuranceRequired === 'yes'
          ? t('submitDocuments.familyInfo.required')
          : t('submitDocuments.familyInfo.notRequired')
      }`
    );
    if (d.insuranceRequired === 'yes') {
      if (d.myNumberLinkStatus) {
        lines.push(
          `${t('submitDocuments.familyInfo.myNumberLink')}: ${t(`submitDocuments.familyInfo.myNumber.${d.myNumberLinkStatus}`)}`
        );
      }
      if (d.supportedDependent === 'yes' || d.supportedDependent === 'no') {
        lines.push(
          `${t('submitDocuments.familyInfo.supportedDependent')}: ${yesNoLabel(t, d.supportedDependent)}`
        );
      }
    }
  }

  return lines.join('\n');
}

/** Age at end of tax year (Dec 31). */
export function ageAtTaxYearEnd(dateOfBirth: string, taxYear: number): number | null {
  const m = dateOfBirth.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  let age = taxYear - y;
  if (mo > 12 || (mo === 12 && d > 31)) return null;
  if (mo > 12 || mo < 1) return null;
  if (mo === 12 && d === 31) return age;
  if (mo > 1 || (mo === 1 && d > 1)) {
    /* still ok */
  }
  const end = new Date(taxYear, 11, 31);
  const birth = new Date(y, mo - 1, d);
  age = end.getFullYear() - birth.getFullYear();
  const beforeBirthday =
    end.getMonth() < birth.getMonth() ||
    (end.getMonth() === birth.getMonth() && end.getDate() < birth.getDate());
  if (beforeBirthday) age -= 1;
  return age;
}
